/** Canonical Auto Sourcing profile serializer — the ONLY place a form state
 * becomes an API payload (Save Profile and Start Search share it). */
import { EMPTY_AUTO_SOURCING_PROFILE } from "./defaultProfile";

const PROFILE_SECTIONS = [
  "scope", "match_strategy", "adjustments", "exclusions", "title_rules", "costs",
  "profitability", "demand", "competition", "availability", "scan_limits", "schedule",
];

// Fields the backend types as numbers. Empty → null (or the default when the
// backend requires a number); numeric strings → numbers; never NaN/Infinity.
const NULLABLE_NUMBERS = {
  scope: ["range_start", "range_end"],
  adjustments: ["fixed_source_cost"],
  profitability: [
    "min_gross_spread", "min_gross_roi", "min_estimated_profit",
    "min_estimated_roi", "min_margin", "max_acquisition_cost",
  ],
  demand: ["min_monthly_sold", "max_monthly_sold", "min_rating", "min_reviews"],
  competition: ["min_seller_count", "max_seller_count"],
  schedule: ["interval_minutes"],
};

const REQUIRED_NUMBERS = {
  scope: ["freshness_hours"],
  adjustments: [
    "store_discount_percent", "cashback_percent", "gift_card_percent",
    "sales_tax_percent", "source_shipping",
  ],
  costs: ["prep_cost", "inbound_shipping", "packaging", "other_cost"],
  scan_limits: [
    "max_products_per_source", "max_sources", "max_provider_requests", "max_duration_minutes",
  ],
};

export function toNumberOrNull(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const text = String(value).trim();
  if (!text) return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

/** Canonical category contract:
 *   null | {mode:"all"} | {mode:"category",category_id,name,url}
 *   | {mode:"custom",user_query,resolver:"raw_query"}
 *   | {mode:"urls"}  (manual category URLs travel in scope.category_urls)
 * An unfinished selection (no category id, blank custom text) is null. */
export function serializeCategorySelection(selection) {
  if (!selection || typeof selection !== "object") return null;
  if (selection.mode === "all") return { mode: "all" };
  if (selection.mode === "urls") return { mode: "urls" };
  if (selection.mode === "category") {
    if (!selection.category_id) return null;
    return {
      mode: "category",
      category_id: String(selection.category_id),
      name: selection.name || null,
      url: selection.url || null,
    };
  }
  if (selection.mode === "custom") {
    const q = String(selection.user_query || "").trim();
    return q ? { mode: "custom", user_query: q, resolver: "raw_query" } : null;
  }
  return null;
}

export function autoSearchName(source, selection) {
  const retailer = String(source?.name || source?.domain || "").trim();
  if (!retailer) return "";
  if (selection?.mode === "category" && selection.name) return `${retailer} — ${selection.name}`;
  if (selection?.mode === "custom" && String(selection.user_query || "").trim()) {
    return `${retailer} — Custom: ${String(selection.user_query).trim()}`;
  }
  if (selection?.mode === "all") return `${retailer} — All`;
  if (selection?.mode === "urls") return `${retailer} — Category URLs`;
  return retailer;
}

function stripUiFields(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj || {})) {
    if (k.startsWith("_")) continue;
    out[k] = v;
  }
  return out;
}

export function serializeAutoSourcingProfile(profile, { mode, source, categoryUrls = [] } = {}) {
  const base = EMPTY_AUTO_SOURCING_PROFILE;
  const payload = {};
  for (const section of PROFILE_SECTIONS) {
    payload[section] = { ...stripUiFields(base[section]), ...stripUiFields(profile?.[section]) };
  }

  for (const [section, keys] of Object.entries(NULLABLE_NUMBERS)) {
    for (const key of keys) payload[section][key] = toNumberOrNull(payload[section][key]);
  }
  for (const [section, keys] of Object.entries(REQUIRED_NUMBERS)) {
    for (const key of keys) {
      const n = toNumberOrNull(payload[section][key]);
      payload[section][key] = n === null ? base[section][key] : n;
    }
  }

  const modeRaw = String(payload.scan_limits?.products_per_source_mode || "custom").toLowerCase();
  payload.scan_limits.products_per_source_mode = modeRaw === "all" ? "all" : "custom";

  const selection = serializeCategorySelection(profile?.scope?.category_selection);
  const scope = payload.scope;
  scope.source_ids = Array.isArray(scope.source_ids) ? scope.source_ids.filter(Boolean).map(String) : [];
  scope.category_selection = selection;
  scope.categories = [];
  scope.category_urls = (categoryUrls || []).map((u) => String(u).trim()).filter(Boolean);
  scope.selected_categories =
    selection?.mode === "category"
      ? [{ category_id: selection.category_id, name: selection.name, url: selection.url }]
      : Array.isArray(profile?.scope?.selected_categories)
        ? profile.scope.selected_categories
            .filter((c) => c && c.category_id)
            .map((c) => ({
              category_id: String(c.category_id),
              name: c.name || null,
              url: c.url || null,
            }))
        : [];
  scope.custom_category_query = selection?.mode === "custom" ? selection.user_query : null;

  // Per-retailer discovery slices (multi-select). Empty map omitted for single-retailer
  // payloads so legacy profiles stay unchanged.
  const rawMap = profile?.scope?.source_scope;
  const sourceScope = {};
  if (rawMap && typeof rawMap === "object") {
    for (const sid of scope.source_ids) {
      const slice = rawMap[sid];
      if (!slice || typeof slice !== "object") continue;
      const sliceSel = serializeCategorySelection(slice.category_selection);
      const selected =
        Array.isArray(slice.selected_categories) && slice.selected_categories.length
          ? slice.selected_categories
              .filter((c) => c && c.category_id)
              .map((c) => ({
                category_id: String(c.category_id),
                name: c.name || null,
                url: c.url || null,
              }))
          : sliceSel?.mode === "category"
            ? [{ category_id: sliceSel.category_id, name: sliceSel.name, url: sliceSel.url }]
            : [];
      sourceScope[sid] = {
        category_selection: sliceSel,
        selected_categories: selected,
        custom_category_query: sliceSel?.mode === "custom" ? sliceSel.user_query : null,
        category_urls: (slice.category_urls || []).map((u) => String(u).trim()).filter(Boolean),
        categories: [],
        max_products:
          slice.max_products == null || slice.max_products === ""
            ? null
            : toNumberOrNull(slice.max_products),
      };
    }
  }
  scope.source_scope = Object.keys(sourceScope).length ? sourceScope : {};

  // When multi-retailer source_scope is present, mirror the first retailer into
  // top-level fields for naming / single-path consumers.
  if (scope.source_ids.length > 1 && scope.source_scope[scope.source_ids[0]]) {
    const first = scope.source_scope[scope.source_ids[0]];
    scope.category_selection = first.category_selection;
    scope.selected_categories = first.selected_categories;
    scope.custom_category_query = first.custom_category_query;
    scope.category_urls = first.category_urls;
  }

  const typedName = String(profile?.name || "").trim();
  let autoName = autoSearchName(source, scope.category_selection);
  if (!typedName && scope.source_ids.length > 1) {
    autoName = `Multi-Retailer Search (${scope.source_ids.length})`;
  }
  payload.name = typedName || autoName || "Auto Sourcing search";
  payload.mode = mode || profile?.mode || "basic";
  payload.enabled = Boolean(profile?.enabled);
  return payload;
}
